import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const ESTADO_UI = {
  dentro: { label: 'Trabajando', bg: 'bg-green-500', dot: 'bg-green-300' },
  pausa:  { label: 'En Pausa',   bg: 'bg-yellow-500', dot: 'bg-yellow-300' },
  fuera:  { label: 'Fuera',      bg: 'bg-slate-600',  dot: 'bg-slate-400' },
};

const BTN_UI = {
  entrada:                { label: '⬛ Fichar Entrada',              cls: 'bg-green-600 hover:bg-green-700 active:bg-green-800' },
  entrada_desplazamiento: { label: '🚗 Entrada + Desplazamiento',    cls: 'bg-teal-600 hover:bg-teal-700 active:bg-teal-800' },
  salida:                 { label: '🔴 Fichar Salida',               cls: 'bg-red-600 hover:bg-red-700 active:bg-red-800' },
  inicio_pausa:           { label: '⏸ Iniciar Pausa',               cls: 'bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700' },
  fin_pausa:              { label: '▶ Volver al Trabajo',            cls: 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' },
};

function getButtons(estado) {
  if (estado === 'fuera')  return ['entrada', 'entrada_desplazamiento'];
  if (estado === 'dentro') return ['inicio_pausa', 'salida'];
  if (estado === 'pausa')  return ['fin_pausa'];
  return [];
}

export default function FichajePanel() {
  const [estado, setEstado] = useState('fuera');
  const [ultimoFichaje, setUltimoFichaje] = useState(null);
  const [now, setNow] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null); // { tipo: 'ok'|'err', texto: '' }

  const fetchEstado = useCallback(async () => {
    try {
      const res = await api.get('/fichajes/estado');
      setEstado(res.data.estado);
      setUltimoFichaje(res.data.ultimoFichaje);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchEstado();
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, [fetchEstado]);

  const fichar = async (tipo) => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await api.post('/fichajes', { tipo });
      setMsg({ tipo: 'ok', texto: res.data.mensaje });
      await fetchEstado();
    } catch (err) {
      setMsg({ tipo: 'err', texto: err.response?.data?.error || 'Error al registrar' });
    } finally {
      setLoading(false);
      setTimeout(() => setMsg(null), 4000);
    }
  };

  const ui = ESTADO_UI[estado];

  const fmtTime = (d) =>
    d.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const fmtDate = (d) =>
    d.toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const fmtFichajeTime = (ts) =>
    ts ? new Date(ts).toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' }) : '';

  const tipoLabel = {
    entrada: 'Entró',
    entrada_desplazamiento: 'Entró (desplazamiento)',
    salida: 'Salió',
    inicio_pausa: 'Pausa',
    fin_pausa: 'Reanudó',
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Estado + reloj */}
      <div className={`${ui.bg} rounded-2xl p-6 text-white shadow-lg`}>
        <div className="flex items-center gap-2 mb-3">
          <span className={`w-3 h-3 rounded-full ${ui.dot} animate-pulse`} />
          <span className="text-lg font-semibold">{ui.label}</span>
        </div>
        <div className="text-5xl font-black tabular-nums tracking-tight">
          {fmtTime(now)}
        </div>
        <div className="mt-1 text-sm opacity-80 capitalize">{fmtDate(now)}</div>
        {ultimoFichaje && (
          <div className="mt-3 text-xs opacity-70">
            Último registro: {tipoLabel[ultimoFichaje.tipo]} a las {fmtFichajeTime(ultimoFichaje.timestamp)}
          </div>
        )}
      </div>

      {/* Mensaje de feedback */}
      {msg && (
        <div className={`px-4 py-3 rounded-xl text-center font-medium text-sm
          ${msg.tipo === 'ok' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {msg.tipo === 'ok' ? '✓ ' : '✗ '}{msg.texto}
        </div>
      )}

      {/* Botones de fichaje */}
      <div className="flex flex-col gap-4">
        {getButtons(estado).map((tipo) => {
          const b = BTN_UI[tipo];
          return (
            <button
              key={tipo}
              onClick={() => fichar(tipo)}
              disabled={loading}
              className={`btn-fichar ${b.cls}`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <span className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                  Registrando...
                </span>
              ) : b.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
